import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, ChevronRight } from 'lucide-react';
import {
  fetchBaganOrganisasi,
  fetchDosenByIdentifier,
  fetchTendikBySlug,
  getImageUrl,
} from '../utils/api';

const PageHero = ({ title, breadcrumbs }) => (
  <div className="page-hero pt-[70px]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 relative z-10">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <div className="flex items-center gap-2 text-white/70 text-sm mb-4">
          <Link to="/" className="hover:text-white transition-colors flex items-center gap-1">
            <Home size={14} /> Beranda
          </Link>
          {breadcrumbs.map((bc, i) => (
            <React.Fragment key={i}>
              <ChevronRight size={14} />
              <span className={i === breadcrumbs.length - 1 ? 'text-white' : ''}>{bc}</span>
            </React.Fragment>
          ))}
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-white font-heading">{title}</h1>
      </motion.div>
    </div>
  </div>
);

const getInitials = (name) =>
  name.replace(/^(Dr\.|Drs\.|Ns\.|Apt\.|apt\.|Hj\.|H\.)\s*/i, '')
    .split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();

const colorMap = {
  purple: 'from-[#7B2FBE] to-[#9333EA]',
  blue:   'from-[#2563EB] to-[#3B82F6]',
  green:  'from-[#16A34A] to-[#22C55E]',
  teal:   'from-[#0D9488] to-[#14B8A6]',
  orange: 'from-[#EA580C] to-[#F97316]',
  gray:   'from-[#6B7280] to-[#9CA3AF]',
  rose:   'from-[#E11D48] to-[#FB7185]',
};

/// Di layar kecil semua kartu dibuat selebar kolomnya supaya terbaca; ukuran
/// tetap ala bagan (yang membedakan tingkatan) baru dipakai mulai lebar lg.
const sizeClasses = {
  xl:  'w-full p-4 lg:w-auto lg:p-5 lg:min-w-[220px] lg:max-w-[220px]',
  lg:  'w-full p-4 lg:w-auto lg:p-4 lg:min-w-[190px] lg:max-w-[190px]',
  md:  'w-full p-4 lg:w-auto lg:p-3 lg:min-w-[160px] lg:max-w-[160px]',
  sm:  'w-full p-4 lg:w-auto lg:p-3 lg:min-w-[140px] lg:max-w-[140px]',
  xs:  'w-full p-4 lg:w-auto lg:p-2 lg:min-w-[130px] lg:max-w-[130px]',
};
const avatarSize = {
  xl: 'w-12 h-12 text-sm lg:w-14 lg:h-14 lg:text-base',
  lg: 'w-12 h-12 text-sm lg:w-12 lg:h-12',
  md: 'w-12 h-12 text-sm lg:w-10 lg:h-10 lg:text-xs',
  sm: 'w-12 h-12 text-sm lg:w-9 lg:h-9 lg:text-xs',
  xs: 'w-12 h-12 text-sm lg:w-8 lg:h-8 lg:text-xs',
};

/// Foto diambil berurutan: unggahan langsung dari panel admin lebih dulu,
/// baru foto profil dosen/tendik dari CMS bila slugnya diisi, dan terakhir
/// inisial nama sebagai cadangan.
const OrgNode = ({ member }) => {
  const { name, position, color = 'gray', size = 'sm', profileType, profileSlug, photoUrl } = member;
  const [cmsPhoto, setCmsPhoto] = useState(null);

  useEffect(() => {
    if (photoUrl || !profileSlug) return undefined;
    let active = true;
    const loader = profileType === 'tendik'
      ? fetchTendikBySlug(profileSlug)
      : fetchDosenByIdentifier(profileSlug);
    Promise.resolve(loader)
      .then((profile) => {
        if (active && profile?.foto) {
          setCmsPhoto(getImageUrl(profile.foto, { width: 160, height: 160, quality: 85 }));
        }
      })
      .catch(() => undefined);
    return () => { active = false; };
  }, [photoUrl, profileSlug, profileType]);

  const photo = photoUrl || cmsPhoto;

  const card = (
    <div className={`${sizeClasses[size] || sizeClasses.sm} rounded-2xl bg-white dark:bg-[#1A182E] border border-purple-100 dark:border-purple-900/20 shadow-sm hover:shadow-lg transition-all text-center inline-block`}>
      <div className={`${avatarSize[size] || avatarSize.sm} rounded-full bg-gradient-to-br ${colorMap[color] || colorMap.gray} flex items-center justify-center text-white font-bold mx-auto mb-2 overflow-hidden`}>
        {photo
          ? <img src={photo} alt={name} loading="lazy" className="w-full h-full object-cover" />
          : getInitials(name)}
      </div>
      <div className="text-sm font-bold text-[#1A1A2E] dark:text-white leading-snug lg:text-xs">{name}</div>
      <div className="text-[11px] text-[#7B2FBE] dark:text-[#9333EA] mt-1 font-medium leading-tight whitespace-pre-line lg:text-[10px]">{position}</div>
    </div>
  );

  if (!profileSlug) return card;
  return (
    <Link to={`/${profileType || 'dosen'}/${profileSlug}`} className="block w-full lg:inline-block lg:w-auto">
      {card}
    </Link>
  );
};

/// Garis penghubung hanya bermakna saat bagan tersusun sebagai pohon di layar
/// lebar; pada layar kecil kartunya bertumpuk ke bawah sehingga garisnya
/// justru mengesankan hubungan yang tidak ada.
const ConnectorV = ({ h = 'h-6' }) => (
  <div className={`hidden lg:block w-0.5 ${h} bg-gradient-to-b from-[#7B2FBE] to-[#9333EA] mx-auto`} />
);

const SectionRow = ({ section, members, index }) => {
  if (members.length === 0) return null;
  // Dua baris teratas (yayasan & pimpinan) tampil sebagai kartu tunggal
  // terpusat tanpa garis penghubung di atasnya.
  const isTopLevel = index < 2;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: Math.min(index * 0.1, 0.9) }}
      className="mb-2"
    >
      {section.label && (
        <div className="flex items-center gap-3 mb-4 mt-8">
          <div className="h-px flex-1 bg-purple-200 dark:bg-purple-900/30" />
          <span className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-widest px-2">{section.label}</span>
          <div className="h-px flex-1 bg-purple-200 dark:bg-purple-900/30" />
        </div>
      )}

      {isTopLevel ? (
        <>
          <div className="flex justify-center">
            {members.map((member) => <OrgNode key={member.id} member={member} />)}
          </div>
          {index === 0 && <ConnectorV h="h-8" />}
        </>
      ) : (
        // Jumlah kolom mengikuti isi barisnya, jadi anggota yang ditambahkan
        // lewat panel admin tetap sejajar dalam satu baris tanpa perlu
        // menyetel apa pun di kode.
        <div
          className="grid grid-cols-1 gap-3 justify-items-center sm:grid-cols-2 lg:[grid-template-columns:repeat(var(--org-cols),minmax(0,1fr))]"
          style={{ '--org-cols': members.length }}
        >
          {members.map((member) => (
            <div key={member.id} className="flex w-full flex-col items-center lg:w-auto">
              <ConnectorV h={section.key === 'sekretaris-prodi' ? 'h-4' : 'h-6'} />
              <OrgNode member={member} />
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

const StrukturOrganisasi = () => {
  const [data, setData] = useState({ sections: [], members: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    fetchBaganOrganisasi()
      .then((result) => { if (active) setData(result); })
      .catch(() => undefined)
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const rows = useMemo(
    () => data.sections.map((section) => ({
      section,
      members: data.members.filter((member) => member.section === section.key),
    })),
    [data.sections, data.members],
  );

  return (
    <div data-testid="struktur-organisasi-page">
      <PageHero title="Struktur Organisasi" breadcrumbs={['Tentang Kami', 'Struktur Organisasi']} />

      <section className="py-16 bg-[#F8F7FF] dark:bg-[#121020] overflow-x-auto">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
            className="text-center text-gray-500 dark:text-gray-400 mb-12 max-w-2xl mx-auto"
          >
            Struktur organisasi STIKES Harapan Ibu Jambi yang menunjukkan hierarki dan garis tanggung jawab dalam pengelolaan institusi.
          </motion.p>

          {loading ? (
            <div className="grid min-h-52 place-items-center text-sm text-gray-400">Memuat bagan…</div>
          ) : data.members.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 py-16 text-center text-sm text-gray-500">
              Data struktur organisasi belum tersedia.
            </div>
          ) : (
            <div className="lg:min-w-[900px]">
              {rows.map(({ section, members }, index) => (
                <SectionRow key={section.key} section={section} members={members} index={index} />
              ))}
            </div>
          )}

          <motion.p
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            className="text-center text-xs text-gray-400 dark:text-gray-600 mt-10"
          >
            * Struktur organisasi dapat berubah sesuai kebutuhan institusi
          </motion.p>
        </div>
      </section>
    </div>
  );
};

export default StrukturOrganisasi;
